import PropTypes from "prop-types";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import GlassCard from "components/GlassCard";
import StandardLoadingIndicator from "components/StandardLoadingIndicator";
import StandardMessageDisplay from "components/StandardMessageDisplay";

const CLIENT_COLOR = "#0075FF";
const COMPETITOR_COLOR = "#2CD9FF";

const firstName = (name) => (name ? name.split(" ")[0] : name);

/**
 * PostingVolumeChart
 *
 * Renders totalPosts per competitor + client, sorted descending.
 * Accepts pre-fetched stats from the parent (same data source as CompetitorEfficiencyChart).
 *
 * stats: array of { id, name, isClient, avgEngagementsPerPost, totalPosts }
 */
function PostingVolumeChart({ stats, loading, error }) {
  const bars = (stats || [])
    .filter((s) => s.totalPosts > 0)
    .sort((a, b) => b.totalPosts - a.totalPosts);

  const maxVal = bars.reduce((m, b) => Math.max(m, b.totalPosts), 0);

  return (
    <GlassCard sx={{ height: "100%" }}>
      <VuiBox mb={1.5}>
        <VuiTypography variant="h6" color="white" fontWeight="bold" sx={{ fontSize: "1rem" }}>
          Posting Volume
        </VuiTypography>
        <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem" }}>
          Total posts published — last quarter
        </VuiTypography>
      </VuiBox>

      {loading ? (
        <StandardLoadingIndicator containerHeight="280px" />
      ) : error ? (
        <StandardMessageDisplay type="error" containerHeight="280px" />
      ) : bars.length === 0 ? (
        <StandardMessageDisplay
          type="noData"
          title="No data"
          message="No post data found for the selected period."
          containerHeight="280px"
        />
      ) : (
        <VuiBox sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
          {bars.map((b) => {
            const barPct = maxVal > 0 ? (b.totalPosts / maxVal) * 100 : 0;
            const color = b.isClient ? CLIENT_COLOR : COMPETITOR_COLOR;
            return (
              <VuiBox key={b.id}>
                <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <VuiBox display="flex" alignItems="center" gap={0.75}>
                    <VuiBox sx={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />
                    <VuiTypography
                      variant="caption"
                      sx={{
                        color: b.isClient ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.75)",
                        fontSize: "0.78rem",
                        fontWeight: b.isClient ? 700 : 500,
                      }}
                    >
                      {firstName(b.name)}
                    </VuiTypography>
                  </VuiBox>
                  <VuiTypography
                    variant="caption"
                    sx={{ fontSize: "0.78rem", fontWeight: 700, color: b.isClient ? "rgba(255,255,255,1)" : "rgba(255,255,255,0.8)" }}
                  >
                    {b.totalPosts}
                  </VuiTypography>
                </VuiBox>
                <VuiBox sx={{ height: "6px", borderRadius: "3px", background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                  <VuiBox sx={{ height: "100%", width: `${barPct}%`, borderRadius: "3px", background: color, transition: "width 0.6s ease" }} />
                </VuiBox>
              </VuiBox>
            );
          })}
          <VuiTypography variant="caption" sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.3)", mt: 0.5 }}>
            ● your company &nbsp;● competitors
          </VuiTypography>
        </VuiBox>
      )}
    </GlassCard>
  );
}

PostingVolumeChart.propTypes = {
  stats: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.bool,
};

export default PostingVolumeChart;
